(ns journal-app-client.utils.css
  (:require [journal-app-client.components.image :as image]
            [reagent.core :as r]
            [cljs.core.async :as async]
            [clojure.string :as s]
            [tick.core :as t]))

(defonce animation-variables (r/atom {}))

(defn animating [animation-key & [is-animating?]]
  (swap! animation-variables assoc animation-key is-animating?))

(defonce system-theme (r/atom :dark))
(defonce user-selected-theme (r/atom :system))
(defn theme [& [selected-theme]]
  (let [selected-theme (or selected-theme @user-selected-theme)]
    (if (= selected-theme :system)
      @system-theme
      selected-theme)))

(defn global-variables []
  {:light {"--color-scheme"                   "only light"
           "--primary-brand-color-hue"        "175"
           "--primary-brand-color-saturation" "41%"
           "--primary-brand-color-lightness"  "77%"

           "--scheme-main-hue"                "0"
           "--scheme-main-saturation"         "0%"
           "--scheme-main-lightness"          "100%"
           "--scheme-invert-hue"              "0"
           "--scheme-invert-saturation"       "0%"
           "--scheme-invert-lightness"        "4%"
           "--scheme-main-bis-hue"            "0"
           "--scheme-main-bis-saturation"     "0%"
           "--scheme-main-bis-lightness"      "98%"
           "--scheme-main-ter-hue"            "0"
           "--scheme-main-ter-saturation"     "0%"
           "--scheme-main-ter-lightness"      "96%"

           "--text-hue"                       "0"
           "--text-saturation"                "0%"
           "--text-lightness"                 "var(--lightness-grey-dark)"
           "--text-light-hue"                 "0"
           "--text-light-saturation"          "0%"
           "--text-light-lightness"           "var(--lightness-grey)"
           "--text-strong-hue"                "0"
           "--text-strong-saturation"         "0%"
           "--text-strong-lightness"          "var(--lightness-grey-darker)"

           "--background-hue"                 "0"
           "--background-saturation"          "0%"
           "--background-lightness"           "93%"
           "--border-hue"                     "0"
           "--border-saturation"              "0%"
           "--border-lightness"               "71%"
           "--border-hover-hue"               "0"
           "--border-hover-saturation"        "0%"
           "--border-hover-lightness"         "48%"
           "--border-light-hue"               "0"
           "--border-light-saturation"        "0%"
           "--border-light-lightness"         "86%"

           "--link-hover-hue"                 "0"
           "--link-hover-saturation"          "0%"
           "--link-hover-lightness"           "21%"
           "--link-hover-border-hue"          "0"
           "--link-hover-border-saturation"   "0%"
           "--link-hover-border-lightness"    "48%"
           "--link-focus-hue"                 "0"
           "--link-focus-saturation"          "0%"
           "--link-focus-lightness"           "21%"
           "--link-active-hue"                "0"
           "--link-active-saturation"         "0%"
           "--link-active-lightness"          "21%"
           "--link-active-border-hue"         "0"
           "--link-active-border-saturation"  "0%"
           "--link-active-border-lightness"   "29%"

           "--navbar-box-shadow-y"            "8px"
           "--box-shadow-y"                   "0.5em"}
   :dark  (let [background-hsl (image/background-hsl)]
            {"--scheme-main-hue"        (first background-hsl)
             "--scheme-main-saturation" (str (second background-hsl) "%")
             "--scheme-main-lightness"  (str (last background-hsl) "%")})})

(defn supports [property value]
  (let [property (cond
                   (keyword? property)
                   (str (name property) ":")
                   (string? property)
                   (let [trimmed (s/trim property)]
                     (if (= (last trimmed) ":")
                       trimmed
                       (str trimmed ":")))
                   :else
                   nil)
        value (when (string? value)
                (s/trim value))]
    (when (and property value)
      (.supports js/CSS (str property " " value)))))

(defn style-transition [transitions]
  (cond
    (string? transitions)
    (str transitions " var(--transition-duration, 250ms) ease")
    (vector? transitions)
    (s/join " var(--transition-duration, 250ms) ease, " transitions)
    :else
    nil))

(defn- set-global-property [property value]
  (js/document.documentElement.style.setProperty property value))
(defn- remove-global-property [property]
  (js/document.documentElement.style.removeProperty property))
(defn- update-global-property [property value]
  (if value
    (set-global-property property value)
    (remove-global-property property)))

(defn set-theme [user-choice]
  (let [current-selected-theme @user-selected-theme]
    (when-not (= current-selected-theme user-choice)
      (reset! user-selected-theme user-choice))
    (when-not (= current-selected-theme (theme user-choice))
      (let [returned-properties-map (get (global-variables) (theme user-choice))
            current-properties-map (reduce
                                     (fn [r v] (assoc r (first v) nil))
                                     {}
                                     (get (global-variables) current-selected-theme))
            properties-map (merge current-properties-map returned-properties-map)]
        (doseq [[property value] properties-map]
          (update-global-property property value))))))

(defn update-system-theme [light?]
  (let [updated-theme (if light? :light :dark)]
    (reset! system-theme updated-theme)
    (when (= @user-selected-theme :system)
      (let [returned-properties-map (get (global-variables) updated-theme)
            current-properties-map (reduce
                                     (fn [r v] (assoc r (first v) nil))
                                     {}
                                     (get (global-variables) (if (not light?) :light :dark)))
            properties-map (merge current-properties-map returned-properties-map)]
        (doseq [[property value] properties-map]
          (update-global-property property value))))))

(defn theme-listener []
  (let [light-theme-media-query (js/window.matchMedia "(prefers-color-scheme: light)")]
    (update-system-theme light-theme-media-query.matches)
    (.addListener light-theme-media-query
      (fn [event] (update-system-theme event.matches)))))

(defonce display-mode (r/atom :browser))

(defn update-display-mode [standalone?]
  (if standalone?
    (reset! display-mode :standalone)
    (reset! display-mode :browser)))

(defn display-mode-listener []
  (let [standalone-media-query (js/window.matchMedia "(display-mode: standalone)")]
    (update-display-mode standalone-media-query.matches)
    (.addListener standalone-media-query
      (fn [event] (update-display-mode event.matches)))))
